/**
 * L'onglet « Communautés WhatsApp » : lecture de l'état et libellés.
 *
 * L'état vient de `public.etat_communautes_whatsapp` (migration
 * `20261017150133_la_communaute_whatsapp.sql`). Une communauté = un centre ×
 * une semaine (son lundi) ; un lien collé par l'administration ; un courriel
 * par famille, quelques jours avant la semaine, qui nomme le groupe (le stage)
 * de chaque enfant. Les familles inscrites ensuite reçoivent l'encart dans
 * leur courriel pré-session.
 *
 * Module pur : aucun appel réseau, testable tel quel.
 */

import { joursEntre } from './preSession';

export interface ReglagesCommunautes {
  /** Allumé : aucune famille n'est écrite, « Envoyer » devient un essai vers l'admin. */
  mode_test: boolean;
  referente_nom: string;
  referente_telephone: string;
  jours_avant: number;
  lot_max: number;
  plafond_quotidien: number;
  pause_jusqu_a: string | null;
  pause_motif: string | null;
}

export interface CommunauteDeLaSemaine {
  id: string;
  lien: string;
  invitations_lancees_le: string | null;
  modifie_le: string;
  envoyees: number;
  /** Dont servies par l'encart du courriel pré-session. */
  par_pre_session: number;
  en_attente: number;
  echecs: number;
  /**
   * Familles toujours attendues qui ont reçu un autre lien que l'actuel : le
   * renvoi les réécrirait (migration 20261019100746). Absent avant elle.
   */
  ancien_lien?: number;
  /** Familles qui ont reçu le lien corrigé. */
  corrigees?: number;
  derniere_erreur: string | null;
}

/** Une semaine d'un centre : une communauté possible. */
export interface SemaineDuCentre {
  center_id: string;
  centre: string;
  /** Le lundi, `AAAA-MM-JJ`. */
  semaine_du: string;
  debut: string;
  fin: string;
  semaine: string;
  periode: string;
  annee: number;
  groupes: string[];
  envoi_possible_le: string;
  familles_attendues: number;
  familles_sans_adresse: number;
  /** Familles attendues (avec adresse) qui n'ont pas reçu l'invitation. */
  familles_sans_invitation: number;
  communaute: CommunauteDeLaSemaine | null;
}

export interface EtatCommunautes {
  reglages: ReglagesCommunautes;
  aujourd_hui: string;
  envoyes_24h: number;
  semaines: SemaineDuCentre[];
}

export interface Planification {
  planifiees: number;
  relancees: number;
  annulees: number;
  deja_servies: number;
  sans_adresse: number;
  a_envoyer: number;
}

/** Le compte rendu du clic « Renvoyer le nouveau lien ». */
export interface Renvoi {
  renvoyees: number;
  a_envoyer: number;
}

export interface ReponseLot {
  raison: 'ok' | 'pause' | 'occupe' | 'plafond' | 'rien' | 'inconnue' | 'mode_test';
  envoyes?: number;
  echecs?: number;
  restantes?: number;
  /** Mode test : le décalage de la tranche suivante, ou null à la fin. */
  suivant?: number | null;
  total?: number;
  envoye_a?: string;
  incident?: { nature: string; message: string } | null;
  pause_jusqu_a?: string | null;
  jusqu_a?: string | null;
  motif?: string | null;
}

/* ------------------------------------------------------------------------ */
/* Le lien                                                                  */
/* ------------------------------------------------------------------------ */

/**
 * Le lien collé, nettoyé comme la base le nettoie
 * (`enregistrer_communaute_whatsapp`) : espaces, `?mode=…` et `#…` que
 * WhatsApp ajoute, barre finale, `https://` manquant. `null` si ce n'est pas
 * un lien d'invitation WhatsApp.
 */
export function normaliserLienWhatsApp(texte: string): string | null {
  let lien = (texte ?? '').trim().replace(/[?#].*$/, '').replace(/\/+$/, '');
  lien = lien.replace(/^(https?:\/\/)?(www\.)?chat\.whatsapp\.com\//i, 'https://chat.whatsapp.com/');
  return /^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9]{16,32}$/.test(lien) ? lien : null;
}

/* ------------------------------------------------------------------------ */
/* Les libellés                                                             */
/* ------------------------------------------------------------------------ */

/** « S1 » → « Semaine 1 ». */
export function libelleSemaine(semaine: string): string {
  const m = /^s\s*(\d+)$/i.exec((semaine ?? '').trim());
  return m ? `Semaine ${Number(m[1])}` : (semaine ?? '').trim();
}

/** « Semaine 1 Auderghem – Automne 2026 », le nom à donner à la communauté. */
export function nomDeLaCommunaute(s: Pick<SemaineDuCentre, 'semaine' | 'centre' | 'periode' | 'annee'>): string {
  const saison = [s.periode, s.annee ? String(s.annee) : ''].filter(Boolean).join(' ');
  const tete = [libelleSemaine(s.semaine), s.centre].filter(Boolean).join(' ');
  return saison ? `${tete} – ${saison}` : tete;
}

function familles(n: number): string {
  return `${n} famille${n > 1 ? 's' : ''}`;
}

export interface SemaineDuCalendrier {
  semaine_du: string;
  centres: SemaineDuCentre[];
}

/** Les centres regroupés par semaine, dans l'ordre ; les centres par nom. */
export function parSemaineDuCalendrier(semaines: SemaineDuCentre[]): SemaineDuCalendrier[] {
  const groupes = new Map<string, SemaineDuCentre[]>();
  for (const s of semaines) groupes.set(s.semaine_du, [...(groupes.get(s.semaine_du) ?? []), s]);
  return [...groupes.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([semaine_du, centres]) => ({
      semaine_du,
      centres: [...centres].sort((x, y) => x.centre.localeCompare(y.centre, 'fr')),
    }));
}

export type SituationCommunaute =
  | 'terminee'
  | 'sans_famille'
  | 'sans_lien'
  | 'trop_tot'
  | 'en_cours'
  | 'a_envoyer'
  | 'nouvelles_familles'
  | 'echecs'
  | 'ancien_lien'
  | 'servie';

/**
 * Les familles attendues que le prochain clic servirait : sans invitation
 * reçue, hors adresses refusées par Gmail (celles-là se relancent à part) et
 * hors invitations déjà en file (comptées dans `en_attente`).
 */
export function nouvellesFamilles(s: SemaineDuCentre): number {
  return Math.max(0, s.familles_sans_invitation - (s.communaute?.echecs ?? 0) - (s.communaute?.en_attente ?? 0));
}

/** Les familles qui ont reçu un ancien lien de la communauté. */
export function famillesAvecAncienLien(s: SemaineDuCentre): number {
  return s.communaute?.ancien_lien ?? 0;
}

/**
 * Où en est une communauté, aujourd'hui (`AAAA-MM-JJ`, heure de Bruxelles).
 * Un ancien lien reçu par des familles passe avant le reste, sauf un envoi en
 * cours : c'est ce qu'il faut corriger.
 */
export function situationCommunaute(s: SemaineDuCentre, aujourdHui: string): SituationCommunaute {
  const sit = situationDeLEnvoi(s, aujourdHui);
  if (sit === 'terminee' || sit === 'sans_famille' || sit === 'sans_lien' || sit === 'en_cours') return sit;
  return famillesAvecAncienLien(s) > 0 ? 'ancien_lien' : sit;
}

/** La situation de l'invitation elle-même, sans regarder les anciens liens. */
function situationDeLEnvoi(s: SemaineDuCentre, aujourdHui: string): Exclude<SituationCommunaute, 'ancien_lien'> {
  if (joursEntre(s.semaine_du, aujourdHui) > 6) return 'terminee';
  if (s.familles_attendues === 0) return 'sans_famille';
  if (!s.communaute) return 'sans_lien';
  if (s.communaute.en_attente > 0) return 'en_cours';
  if (aujourdHui < s.envoi_possible_le) return 'trop_tot';
  if (!s.communaute.invitations_lancees_le) return 'a_envoyer';
  if (nouvellesFamilles(s) > 0) return 'nouvelles_familles';
  if (s.communaute.echecs > 0) return 'echecs';
  return 'servie';
}

/** Le bouton « Envoyer » a-t-il un sens ? */
export function envoiPossible(situation: SituationCommunaute): boolean {
  return situation === 'a_envoyer' || situation === 'nouvelles_familles' || situation === 'en_cours';
}

export function libelleSituationCommunaute(s: SemaineDuCentre, aujourdHui: string): string {
  const c = s.communaute;
  switch (situationCommunaute(s, aujourdHui)) {
    case 'terminee':
      return 'Semaine terminée';
    case 'sans_famille':
      return 'Aucune famille attendue pour le moment';
    case 'sans_lien':
      return 'Lien à coller';
    case 'trop_tot':
      return `Prêt : l'envoi s'ouvre le ${jourMois(s.envoi_possible_le)}`;
    case 'en_cours':
      return `Envoi en cours : ${c?.en_attente ?? 0} en attente`;
    case 'a_envoyer':
      return `À envoyer : ${familles(s.familles_sans_invitation)}`;
    case 'nouvelles_familles': {
      const n = nouvellesFamilles(s);
      return `${familles(n)} inscrite${n > 1 ? 's' : ''} depuis l'envoi`;
    }
    case 'ancien_lien': {
      const n = famillesAvecAncienLien(s);
      return `${familles(n)} ${n > 1 ? 'ont' : 'a'} reçu un ancien lien`;
    }
    case 'echecs':
      return `${c?.echecs ?? 0} adresse${(c?.echecs ?? 0) > 1 ? 's' : ''} refusée${(c?.echecs ?? 0) > 1 ? 's' : ''} par Gmail`;
    case 'servie':
      return 'Toutes les familles sont invitées';
  }
}

/** `2026-10-12` → `12/10`. */
export function jourMois(date: string): string {
  const [, m, j] = date.slice(0, 10).split('-');
  return `${j}/${m}`;
}

/** Le compte rendu du clic, avant les lots. */
export function resumePlanification(p: Planification): string {
  const parts = [`${p.a_envoyer} invitation${p.a_envoyer > 1 ? 's' : ''} à envoyer`];
  if (p.deja_servies > 0) parts.push(`${familles(p.deja_servies)} déjà invitée${p.deja_servies > 1 ? 's' : ''}`);
  if (p.sans_adresse > 0) parts.push(`${familles(p.sans_adresse)} sans adresse email (voir la liste de contrôle)`);
  return parts.join(' · ');
}

/**
 * Ce que l'écran fait après un lot : continuer après une pause, attendre que
 * l'autre lot finisse, ou s'arrêter avec un message.
 */
export function suiteDuLot(r: ReponseLot): { continuer: boolean; attendreSecondes: number; message: string | null } {
  switch (r.raison) {
    case 'ok':
      if (r.incident) {
        return { continuer: false, attendreSecondes: 0, message: `Gmail a freiné l'envoi (${r.incident.message}). Les invitations restantes attendent : reprenez plus tard.` };
      }
      return (r.restantes ?? 0) > 0
        ? { continuer: true, attendreSecondes: 60, message: null }
        : { continuer: false, attendreSecondes: 0, message: null };
    case 'occupe':
      return { continuer: true, attendreSecondes: 30, message: null };
    case 'pause':
      return { continuer: false, attendreSecondes: 0, message: `Gmail a freiné les envois${r.jusqu_a ? ` : reprise possible à ${heureMinute(r.jusqu_a)}` : ''}.` };
    case 'plafond':
      return { continuer: false, attendreSecondes: 0, message: 'Plafond de 24 h atteint : les invitations restantes partiront demain.' };
    case 'rien':
      return { continuer: false, attendreSecondes: 0, message: null };
    case 'mode_test':
      return { continuer: false, attendreSecondes: 0, message: 'Le mode test est allumé : aucune invitation ne part aux familles.' };
    default:
      return { continuer: false, attendreSecondes: 0, message: 'Communauté introuvable.' };
  }
}

export interface ActionPrincipale {
  geste: 'envoyer' | 'essai';
  libelle: string;
  /** Combien de courriels le clic ferait partir. */
  n: number;
  /** Le bouton se montre grisé : ce qu'il manque pour pouvoir cliquer. */
  bloquee?: string;
}

/**
 * Le bouton principal d'une carte. En mode test, un essai : un courriel
 * « [TEST] » par famille attendue, à l'admin, quelle que soit la fenêtre.
 * Sinon, l'envoi réel, dans la fenêtre, aux familles qui n'ont rien reçu.
 *
 * Sans lien enregistré, l'essai se montre grisé plutôt que de manquer : le
 * courriel donne le lien, il n'y a rien à essayer sans lui.
 */
export function actionPrincipale(s: SemaineDuCentre, aujourdHui: string, modeTest: boolean): ActionPrincipale | null {
  const sit = situationDeLEnvoi(s, aujourdHui);
  if (sit === 'terminee' || s.familles_attendues === 0) return null;
  if (modeTest) {
    const essai: ActionPrincipale = { geste: 'essai', libelle: `Essai — ${s.familles_attendues} courriel${s.familles_attendues > 1 ? 's' : ''} à moi`, n: s.familles_attendues };
    return s.communaute ? essai : { ...essai, bloquee: "Enregistrez d'abord le lien de la communauté : l'aperçu et l'essai s'ouvrent ensuite." };
  }
  if (!s.communaute) return null;
  const n = nouvellesFamilles(s) + s.communaute.en_attente;
  if (!envoiPossible(sit) || n === 0) return null;
  const libelle = sit === 'en_cours' ? `Reprendre l'envoi (${n})`
    : sit === 'nouvelles_familles' ? `Inviter les ${n} nouvelle(s) famille(s)`
      : `Envoyer — ${n} famille(s)`;
  return { geste: 'envoyer', libelle, n };
}

/**
 * Le bouton « Renvoyer le nouveau lien » : aux familles qui ont reçu un ancien
 * lien, jusqu'au dimanche de la semaine. En mode test, grisé : la base refuse.
 */
export function renvoiDuLien(s: SemaineDuCentre, aujourdHui: string, modeTest: boolean): { libelle: string; n: number; bloquee?: string } | null {
  const n = famillesAvecAncienLien(s);
  if (!s.communaute || n === 0 || situationCommunaute(s, aujourdHui) === 'terminee') return null;
  const renvoi = { libelle: `Renvoyer le nouveau lien (${n})`, n };
  return modeTest ? { ...renvoi, bloquee: 'Éteignez le mode test pour renvoyer le lien aux familles.' } : renvoi;
}

/** Le compte rendu du renvoi, avant les lots. */
export function resumeRenvoi(r: Renvoi): string {
  if (r.renvoyees === 0) return 'Aucune famille à réécrire : toutes ont déjà le lien actuel.';
  return `${familles(r.renvoyees)} ${r.renvoyees > 1 ? 'vont' : 'va'} recevoir le lien corrigé`
    + (r.a_envoyer > r.renvoyees ? ` · ${r.a_envoyer} courriel${r.a_envoyer > 1 ? 's' : ''} en file au total` : '');
}

function heureMinute(iso: string): string {
  return new Date(iso).toLocaleString('fr-BE', {
    timeZone: 'Europe/Brussels', weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/* ------------------------------------------------------------------------ */
/* La liste de contrôle                                                     */
/* ------------------------------------------------------------------------ */

export interface FamilleDeControle {
  user_id: string;
  parent_nom: string;
  parent_prenom: string;
  telephone: string;
  telephone2: string;
  email: string;
  enfants: string;
  invitation: 'a_envoyer' | 'en_cours' | 'envoye' | 'echec' | 'annule' | null;
  invitation_canal: 'invitation' | 'pre_session' | null;
  invitation_le: string | null;
}

export interface ListeDeControle {
  semaine: Pick<SemaineDuCentre, 'centre' | 'semaine' | 'periode' | 'annee' | 'debut' | 'fin' | 'semaine_du'>;
  groupes: { groupe: string; familles: FamilleDeControle[] }[];
}

export function libelleInvitation(f: Pick<FamilleDeControle, 'invitation' | 'invitation_canal'>): string {
  switch (f.invitation) {
    case 'envoye':
      return f.invitation_canal === 'pre_session' ? 'Reçue (courriel pré-session)' : 'Reçue';
    case 'a_envoyer':
    case 'en_cours':
      return 'En cours';
    case 'echec':
      return 'Échec';
    default:
      return 'Pas reçue';
  }
}

function cellule(v: string): string {
  return /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * La liste en CSV, séparateur « ; » et BOM UTF-8 : Excel en français l'ouvre
 * tel quel, accents compris.
 */
export function listeDeControleCsv(liste: ListeDeControle): string {
  const lignes = [['Groupe', 'Nom du parent', 'Prénom du parent', 'Téléphone', 'Téléphone 2', 'Email', 'Enfant(s)', 'Invitation']];
  for (const g of liste.groupes) {
    for (const f of g.familles) {
      lignes.push([g.groupe, f.parent_nom, f.parent_prenom, f.telephone, f.telephone2, f.email, f.enfants, libelleInvitation(f)]);
    }
  }
  return '﻿' + lignes.map((l) => l.map(cellule).join(';')).join('\r\n') + '\r\n';
}

/** `liste-whatsapp-auderghem-2026-10-19.csv` */
export function nomDuFichierCsv(liste: ListeDeControle): string {
  const centre = liste.semaine.centre
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `liste-whatsapp-${centre || 'centre'}-${liste.semaine.semaine_du}.csv`;
}

/* ------------------------------------------------------------------------ */
/* Les erreurs                                                              */
/* ------------------------------------------------------------------------ */

/** Une erreur de l'API, dite en clair. */
export function erreurEnClair(err: unknown, absente = 'Les communautés WhatsApp ne sont pas encore installées sur cette base (migration 20261017150133_la_communaute_whatsapp).'): string {
  const o = (err ?? {}) as { code?: string; message?: string };
  if (o.code === 'PGRST202' || o.code === 'PGRST205') return absente;
  if (o.code === '42501') return "Vous n'avez pas les droits pour cette action.";
  // Les refus de la base portent une étiquette entre crochets : on la retire.
  return (o.message ?? '').replace(/^\[[a-z-]+\]\s*/, '') || 'Erreur inconnue';
}
